<script lang="ts">
	import { tick } from 'svelte'
	import { trackElement } from '#lib/utils/domActions.js'
	import CounterBadge from '#lib/atoms/CounterBadge.svelte'

	interface Tab {
		label: string
		disabled?: boolean
		id?: string
		counter?: number // optional property, if given shows a small badge with the counter value
	}

	let {
		activeIndex = $bindable(),
		activeId = $bindable(),
		ariaLabel = 'Tab navigation',
		tabs,
		justify = 'center',
		onChange,
		...restProps
	}: {
		activeIndex?: number
		activeId?: string
		ariaLabel?: string
		tabs: Tab[]
		justify?: 'start' | 'center' | 'end'
		onChange?: (index: number) => void
	} = $props()

	let navigationElements: HTMLElement | undefined = $state()
	let buttonElements: (HTMLElement | undefined)[] = $state([])

	const firstEnabled = $derived.by(() => {
		const index = tabs.findIndex((t) => !t.disabled)
		return index === -1 ? undefined : index
	})
	// falls back to activeId when activeIndex is stale/disabled, not just when undefined
	const current = $derived.by(() => {
		const activeIndexTab = activeIndex !== undefined ? tabs[activeIndex] : undefined
		if (activeIndexTab && !activeIndexTab.disabled) {
			return activeIndex
		}

		/**
		 * activeId and tab.id are both undefined when absent (matches their optional
		 * types), so `tab.id === activeId` would match an anonymous tab when activeId
		 * is itself undefined
		 */
		if (activeId === undefined) return firstEnabled

		const activeIndexById = tabs.findIndex((tab) => tab.id === activeId)
		const activeIdTab = tabs[activeIndexById]
		return activeIdTab && !activeIdTab.disabled ? activeIndexById : firstEnabled
	})

	/**
	 * We break the "no state writes in `$effect`" rule here on purpose.
	 * This picks a valid tab when there's no selection yet, or when
	 * `activeIndex` / `activeId` points to a tab that's disabled or gone.
	 * We can't use `$derived` here because we also need to call `onChange`.
	 * Please don't change this to a `$derived`.
	 */
	$effect(() => {
		const activeIndexTab = activeIndex !== undefined ? tabs[activeIndex] : undefined

		/**
		 * same undefined-matches-anonymous-tab guard as `current` above. falling back
		 * to undefined (not a real tab) is safe: idInvalid's `!activeIdTab` treats it
		 * the same as "no match found", which is exactly what an unset activeId means
		 */
		const activeIdTab = activeId === undefined ? undefined : tabs.find((tab) => tab.id === activeId)
		const indexInvalid = activeIndex === undefined || !activeIndexTab || activeIndexTab.disabled
		const idInvalid = activeId === undefined || !activeIdTab || activeIdTab.disabled

		if (!indexInvalid && !idInvalid) return

		const index = current
		if (index === undefined || index < 0 || tabs[index]?.disabled) return

		if (activeIndex !== index || activeId !== tabs[index]?.id) {
			activeIndex = index
			activeId = tabs[index]?.id
			onChange?.(index)
		}
	})

	const select = (index: number) => {
		if (tabs[index]?.disabled || current === index) return
		activeIndex = index
		activeId = tabs[index]?.id
		onChange?.(index)
	}

	const onKeydown = (event: KeyboardEvent) => {
		if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return
		if (current === undefined) return
		event.preventDefault()

		let next = current
		if (event.key === 'Home') next = 0
		else if (event.key === 'End') next = tabs.length - 1
		else if (event.key === 'ArrowRight') next = (current + 1) % tabs.length
		else if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length

		// skip disabled tabs
		const dir = event.key === 'ArrowLeft' ? -1 : 1
		let guard = 0
		while (guard < tabs.length && tabs[next]?.disabled) {
			next = (next + dir + tabs.length) % tabs.length
			guard++
		}
		if (guard >= tabs.length) return

		select(next)
		tick().then(() => buttonElements[next]?.focus())
	}
</script>

<nav
	class="tab-menu"
	bind:this={navigationElements}
	aria-label={ariaLabel}
	style:--tab-justify={justify}
	{...restProps}
>
	<ul role="tablist" onkeydown={onKeydown}>
		{#each tabs as tab, i (tab.id ?? i)}
			<li role="presentation">
				<button
					bind:this={buttonElements[i]}
					type="button"
					role="tab"
					id={tab.id}
					disabled={tab.disabled}
					aria-selected={current === i}
					tabindex={current === i ? 0 : -1}
					class:is-active={current === i}
					onclick={() => select(i)}
				>
					{tab.label}
					{#if tab.counter !== undefined && tab.counter !== null}
						<CounterBadge count={tab.counter} active={current === i} />
					{/if}
				</button>
			</li>
		{/each}
	</ul>
	<span
		class="indicator"
		aria-hidden="true"
		{@attach trackElement({
			target: () => (current !== undefined ? buttonElements[current] : undefined),
			container: () => navigationElements,
			vars: { x: '--tab-indicator-x', width: '--tab-indicator-width' }
		})}
	></span>
</nav>

<style lang="scss">
	@import '$styles/global';

	.tab-menu {
		--tab-accent: var(--menu-tab-accent, #32b24a);
		--tab-text-color: var(--menu-tab-text-color, black);
		--tab-text-muted: var(--menu-tab-muted, var(--color-gray-600));

		position: relative;
		border-bottom: 1px solid var(--menu-tab-border, var(--color-gray-100));

		& ul {
			display: flex;
			gap: 28px;
			list-style: none;
			margin: 0;
			padding: 0;
			justify-content: var(--tab-justify, start);
		}

		& ul > li > button {
			align-items: center;
			background: transparent;
			border: 0;
			cursor: pointer;
			display: inline-flex;
			column-gap: 6px;

			@include roboto-font(normal, 500, 1rem, var(--tab-text-color));

			transition: color 160ms ease;
			padding: 4px 4px 8px;
			border-radius: 4px;
		}

		& ul > li > button:not(:disabled):hover {
			background-color: var(--color-green-100);
		}

		& ul > li > button:disabled {
			color: var(--tab-text-muted);
			cursor: not-allowed;
		}

		& .indicator {
			position: absolute;
			left: 0;
			bottom: 0;
			height: 4px;
			width: var(--tab-indicator-width);
			background: var(--tab-accent);
			border-radius: 999px;
			transform: translateX(var(--tab-indicator-x));
			transition:
				transform 220ms ease,
				width 220ms ease;
		}
	}

	@media (width <= 640px) {
		.tab-menu ul {
			gap: 18px;
		}
	}
</style>
